import { Injectable, inject, computed, Signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";

import { LanguageService, Model } from "../../../shared";
import { LanguagePipe } from "../../../shared/pipes";
import { OnlineModel } from "../../../shared/models/online.model";
import { ApplicationState } from "../../../shared/state/state/application.state";
import { CreateFoundMedicationIncidentBody, FoundMedicationIncident } from "../../../shared/types";
import { generateDateOptions } from "../../util/date-options";
import { Option } from "../../components/single-select-list/single-select-list.component";
import { AWSHttpClient } from "@intocare-medapp/aws/http";
import { selectFoundMedicationIncidentsState } from "app/shared/state/selectors/patient-medication.selectors";

export const OTHER_ROOM = Symbol("other_room");
export const OTHER_DEPARTMENT = Symbol("other_department");
export type RoomValue = string | typeof OTHER_ROOM;
export type DepartmentValue = string | typeof OTHER_DEPARTMENT;

@Injectable({
	providedIn: "root",
})
export class FoundMedicationIncidentModel extends Model {
	private readonly translateService = inject(TranslateService);
	private readonly languageService = inject(LanguageService);
	private readonly languagePipe = inject(LanguagePipe);
	private readonly onlineModel = inject(OnlineModel);
	private readonly store: Store<ApplicationState> = inject(Store);
	private readonly httpClient = inject(AWSHttpClient);

	private readonly departments = toSignal(this.store.select((state) => state.data.groups));
	foundMedicationIncidents = toSignal(this.store.select(selectFoundMedicationIncidentsState));

	// @ts-expect-error TS2322
	public readonly departmentsOptions: Signal<Option<DepartmentValue>[]> = computed(() => {
		const departments = this.departments();
		return (
			departments && [
				{
					label: this.translateService.instant(
						"medication.found_medication_incidents.steps.date_location.options.other",
					) as string,
					value: OTHER_DEPARTMENT,
				},
				...departments.map((department) => ({
					label: this.languagePipe.transform(department.name) as string,
					value: department.id,
				})),
			]
		);
	});

	public createFoundMedicationIncident(incident: CreateFoundMedicationIncidentBody) {
		return this.httpClient.post<CreateFoundMedicationIncidentBody>("/medication/found-medication-incidents", incident);
	}

	public createFoundMedicationIncidentUserReadConfirmation(incidentId: string, userId: string) {
		return this.httpClient.post<void>("/user-read-confirmation", {
			incidentId,
			userId,
			type: "found-medication-incident",
		});
	}

	public getFoundMedicationIncident(id: string): Observable<FoundMedicationIncident> {
		return this.httpClient.get<FoundMedicationIncident>(`/medication/found-medication-incidents/${id}`);
	}

	public generateDateOptions(): Option<string>[] {
		return generateDateOptions(this.languageService.language(), 4);
	}

	public getRoomOptionsForDepartment(departmentId: string): Option<RoomValue>[] {
		// @ts-expect-error TS2532
		const department = this.departments().find((department) => department.id === departmentId);
		if (!department) {
			// @ts-expect-error TS2322
			return undefined;
		}
		return [
			{
				label: this.translateService.instant(
					"medication.found_medication_incidents.steps.date_location.options.other",
				) as string,
				value: OTHER_ROOM,
			},
			...department.rooms.map((room) => ({
				label: room,
				value: room,
			})),
		];
	}

	public checkOnlineShowDialog(navigationRoute?: string[]): Observable<boolean> {
		return this.onlineModel.checkOnlineShowDialog({
			dialogData: {
				title: "medication.offline.title",
				content: "medication.offline.content",
				cancelButtonText: "medication.offline.cancel_button",
			},
			navigationRoute,
			disableClose: true,
		});
	}
}
